/**
 * AudioManager — orchestrates YouTube players and proximity crossfading.
 *
 * Owns a Map<trackId, YouTubePlayer>.
 * On each tick, evaluates proximity and crossfades volumes.
 * Uses the zustand store for state synchronization.
 */

import { YouTubePlayer } from './YouTubePlayer';
import { ProximityEngine } from './ProximityEngine';
import type { CityData, Track } from '../types/city';
import { useGameStore } from '../store/gameStore';

export class AudioManager {
  players: Map<string, YouTubePlayer> = new Map();
  engine: ProximityEngine;
  tracks: Track[] = [];
  buildings: Array<{ trackId: string; position: [number, number, number] }> = [];
  tickInterval: ReturnType<typeof setInterval> | null = null;
  initialized = false;
  private loadingMutex = false;

  constructor() {
    this.engine = new ProximityEngine(20, 2); // buildings ~12-18 apart, need 20 to reach at spawn
  }

  /** Initialize from city data — creates player stubs (NOT loaded) */
  async init(cityData: CityData): Promise<void> {
    if (this.initialized) return;

    this.tracks = cityData.districts.flatMap((d) => d.tracks);

    for (const track of this.tracks) {
      const player = new YouTubePlayer(track.id, track.embedUrl);
      this.players.set(track.id, player);
    }

    this.initialized = true;
    console.log(`[AudioManager] Initialized with ${this.tracks.length} tracks`);
  }

  /** Preload all players sequentially — avoids WebGL context loss on mobile from 5+ simultaneous YT iframes */
  async preloadAll(): Promise<void> {
    const players = Array.from(this.players.values());
    for (let i = 0; i < players.length; i++) {
      try {
        await players[i].load();
        // Stagger loads: 800ms gap prevents mobile Chrome from OOM/context-loss
        if (i < players.length - 1) {
          await new Promise((r) => setTimeout(r, 800));
        }
      } catch (e) {
        console.warn(`[AudioManager] Preload failed for player ${i}:`, e);
      }
    }
    console.log('[AudioManager] All players preloaded');
  }

  /** Unlock all loaded players (must be called from user gesture) */
  async unlockAll(): Promise<void> {
    for (const player of this.players.values()) {
      if (player.loaded && !player.unlocked) {
        await player.unlock();
      }
    }
  }

  /** Register building positions (called after scene renders buildings) */
  registerBuildings(
    buildings: Array<{ trackId: string; position: [number, number, number] }>,
  ): void {
    this.buildings = buildings;
  }

  /** Start the proximity evaluation loop */
  start(): void {
    if (this.tickInterval) return;
    this.tickInterval = setInterval(() => this.tick(), 200); // 5Hz
    console.log('[AudioManager] Proximity loop started');
  }

  /** Stop the loop and pause all players */
  stop(): void {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    for (const player of this.players.values()) {
      player.pause();
    }
  }

  /** Single tick — evaluate proximity and adjust volumes */
  private async tick(): Promise<void> {
    if (this.buildings.length === 0) return;

    const { playerPosition } = useGameStore.getState();
    const store = useGameStore.getState();

    // If a track is locked, only play that one — skip proximity
    if (store.lockedTrackId) {
      for (const [tid, player] of this.players) {
        if (tid === store.lockedTrackId) {
          if (!player.playing) player.play();
          player.setVolume(100);
        } else {
          if (player.playing) player.pause();
        }
      }
      store.setActiveTrack(store.lockedTrackId, 1);
      return;
    }

    const nearby = this.engine.evaluate(playerPosition, this.buildings);

    const activeSet = new Set(nearby.map((n) => n.trackId));

    store.setNearbyBuildings?.(nearby.map((n) => n.trackId));

    // For each nearby building, ensure player is loaded and adjust volume
    for (const item of nearby) {
      const player = this.players.get(item.trackId);
      if (!player) continue;

      // Lazy-load the player (one at a time to avoid mobile WebGL context loss)
      if (!player.loaded) {
        if (this.loadingMutex) continue; // another load in progress — try next tick
        this.loadingMutex = true;
        try {
          await player.load();
        } catch (e) {
          console.warn(`[AudioManager] Failed to load ${item.trackId}:`, e);
          this.loadingMutex = false;
          continue;
        }
        this.loadingMutex = false;
      }

      const volume = this.engine.volumeForDistance(item.distance);

      if (volume > 0) {
        if (!player.playing) {
          player.play();
        }
        player.setVolume(volume);
      } else {
        if (player.playing) {
          player.pause();
        }
      }
    }

    // Pause players that are no longer nearby
    for (const [trackId, player] of this.players) {
      if (!activeSet.has(trackId) && player.playing) {
        player.pause();
      }
    }

    // Update active track in store (closest one)
    const closest = nearby[0];
    if (closest) {
      const volume = this.engine.volumeForDistance(closest.distance);
      store.setActiveTrack(closest.trackId, volume / 100);
    } else {
      store.setActiveTrack(null, 0);
    }
  }

  /** Get a track by ID */
  getTrack(id: string): Track | undefined {
    return this.tracks.find((t) => t.id === id);
  }

  destroy(): void {
    this.stop();
    for (const player of this.players.values()) {
      player.destroy();
    }
    this.players.clear();
  }
}

// Singleton
export const audioManager = new AudioManager();
